import type { Position, State, NodeRendererContext } from './diagram.types';
import { useResizeObserver } from '../hooks/UNSAFE_useResizeObserver';
import { useCallback, useRef } from 'preact/hooks';
import { getItemAriaLabel } from './utils/diagramUtils';
import { classNames } from '../utils/UNSAFE_classNames';
import { itemStyles } from './themes/DiagramStyles.css';
import { ComponentChildren } from 'preact';
import { useTranslationBundle } from '../hooks/UNSAFE_useTranslationBundle';
import type { BundleType } from '../resources/nls/bundle';

/**
 * Props for node component
 */
type NodeProps<K1, D1> = {
  id: K1;
  position: Position;
  label?: string;
  state: State;
  previousState: State;
  isDimmed?: boolean;
  activeId?: string;
  accessibleLabel?: string;
  nodeIndex: number;
  data: D1;
  nodeRenderer: (context: NodeRendererContext<D1>) => ComponentChildren;
  onNodeSizeChanged: (width: number, height: number, idx: number) => void;
  supportsSelection: boolean;
};

export const DiagramNodeElement = <K1, D1>({
  id,
  state,
  previousState,
  position,
  activeId,
  nodeRenderer,
  onNodeSizeChanged,
  nodeIndex,
  data,
  accessibleLabel,
  isDimmed,
  supportsSelection
}: NodeProps<K1, D1>) => {
  const { nodeStyles, dimmedItemStyle } = itemStyles;
  const context = {
    state: state,
    previousState: previousState,
    data: data
  };

  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const ref = useRef<HTMLDivElement>(null);
  // TODO JET-62472 change to one resize observer function
  const optionsRef = useRef({ box: 'border-box' as const });
  useResizeObserver(
    ref,
    useCallback(
      (entry: ResizeObserverEntry) => {
        const boxSize = entry.borderBoxSize;
        const width = boxSize[0].inlineSize;
        const height = boxSize[0].blockSize;
        onNodeSizeChanged(width, height, nodeIndex);
      },
      [nodeIndex, onNodeSizeChanged]
    ),
    optionsRef.current
  );
  return (
    <div
      ref={ref}
      key={id}
      class={classNames([nodeStyles, isDimmed ? dimmedItemStyle : undefined])}
      data-node={id}
      role="img"
      aria-label={getItemAriaLabel(
        translations,
        supportsSelection,
        state.selected,
        accessibleLabel
      )}
      style={{
        top: position.y,
        left: position.x
      }}
      id={state.focused || state.hovered ? activeId : undefined}>
      {nodeRenderer(context)}
    </div>
  );
};
